// Global
import React, { useEffect } from 'react';
import { useRouter } from 'next/router';

// Lib
import { ComponentParams, ComponentRendering } from '@sitecore-content-sdk/nextjs';
import { UserProfile } from '@generated/UserProfile/SubscriptionCenter.model';
import SubscriptionCenter from './SubscriptionCenter';
import { EmailSubscriptionProps } from '../MyAccount/EmailSubscription/EmailSubscription';
import { usePlainTextWrapper } from 'components/helpers/SitecoreWrappers/PlainTextWrapper/PlainTextWrapper';
import { useRichTextWrapper } from 'components/helpers/SitecoreWrappers/RichTextWrapper/RichTextWrapper';
import { useSiteSettings } from 'lib/hooks/useSiteSettings';
import useDictionary from 'lib/hooks/useDictionary';
import { useSessionSafe } from 'lib/hooks/auth/useSessionSafe';
import useIsEditing from 'lib/hooks/sitecore/useIsEditing';

const UNSUBSCRIBE_REDIRECT_URL = '/emailsubscription/unsubscribe';

type SubscriptionCenterComponentProps = {
  rendering: ComponentRendering & { params: ComponentParams };
  params: ComponentParams;
  fields?: UserProfile.SubscriptionCenter.SubscriptionCenter['fields'];
};

const SubscriptionCenterSitecore = (props: SubscriptionCenterComponentProps): JSX.Element => {
  const isEditing = useIsEditing();
  const { data: session, status } = useSessionSafe();
  const siteSettings = useSiteSettings();
  const { getDictionaryValue } = useDictionary();
  const router = useRouter();

  // If the user is not signed in, redirect to the email unsubscribe page.
  useEffect(() => {
    if (!isEditing && status === 'unauthenticated') {
      router.push(UNSUBSCRIBE_REDIRECT_URL);
    }
  }, [isEditing, status, router]);

  const emailSubscriptionFields = props.fields;
  const newsLetterSubscriptionId = siteSettings?.subscriptionID?.value;
  const userEmail = session?.user?.email ?? undefined;

  const isSubscribed = session?.user?.subscriptions?.find(
    (subscription) =>
      subscription.SubscriptionId === newsLetterSubscriptionId && subscription.Subscribed
  );

  // Build the EmailSubscription props using the same logic as MyAccount.sitecore.tsx
  const emailSubscription: EmailSubscriptionProps = {
    title: usePlainTextWrapper({ field: emailSubscriptionFields?.sectionTitle }),
    sectionDescription: useRichTextWrapper({ field: emailSubscriptionFields?.sectionDescription }),
    description: useRichTextWrapper({
      field: emailSubscriptionFields?.description,
      tokens: [
        {
          key: '${subscriptionToUnsubscribeFrom}',
          value: isSubscribed
            ? getDictionaryValue('Components.MyAccount.UnsubscribeFrom')?.toLowerCase()
            : getDictionaryValue('Components.MyAccount.SubscribeTo')?.toLowerCase(),
        },
      ],
    }),
    errorMessage: emailSubscriptionFields?.errorMessage?.value || '',
    errorSecondaryMessage: emailSubscriptionFields?.errorSecondaryMessage?.value || '',
    successMessage: usePlainTextWrapper({
      field: emailSubscriptionFields?.successMessage,
      tokens: [
        {
          key: '${subscribedToUnsubscribedFromSuccessText}',
          value: isSubscribed
            ? getDictionaryValue('Components.MyAccount.SubscribedTo')?.toLowerCase()
            : getDictionaryValue('Components.MyAccount.UnsubscribedFrom')?.toLowerCase(),
        },
      ],
    }),
    subscribeButtonText: usePlainTextWrapper({
      field: emailSubscriptionFields?.subscribeButtonText,
    }),
    subscribeButtonAriaLabel: emailSubscriptionFields?.subscribeButtonText?.value,
    unsubscribeButtonText: usePlainTextWrapper({
      field: emailSubscriptionFields?.unsubscribeButtonText,
    }),
    unsubscribeButtonAriaLabel: emailSubscriptionFields?.unsubscribeButtonText?.value,
    newsLetterSubscriptionId,
    isSubscribedEmailSubscription: !!isSubscribed,
  };
  const heading = useRichTextWrapper({ field: props.fields?.heading });
  const topDescription = useRichTextWrapper({ field: props.fields?.topDescription });
  const emailLabel = usePlainTextWrapper({ field: props.fields?.emailText });
  const noteLink = useRichTextWrapper({ field: props.fields?.noteLink });
  const unsubscribeConfirmationFields = {
    confirmationText:
      useRichTextWrapper({ field: props.fields?.unsubscribeConfirmationText }) || null,
    buttonText: props.fields?.unsubscribeConfirmationButtonText?.value,
    cancelText: props.fields?.cancelText?.value,
    unsubscribeSuccessText: props.fields?.unsubscribeSuccessText?.value,
    unsubscribeFailureText: props.fields?.unsubscribeFailureText?.value,
  };

  if (!props.fields && !isEditing) {
    return <></>;
  }

  // Avoid rendering protected content while unauthenticated (redirect handled above).
  if (!isEditing && status === 'unauthenticated') {
    return <></>;
  }

  return (
    <SubscriptionCenter
      heading={heading}
      topDescription={topDescription}
      emailLabel={emailLabel}
      userEmail={userEmail}
      noteLink={noteLink}
      unsubscribeConfirmation={unsubscribeConfirmationFields}
      emailSubscription={emailSubscription}
    />
  );
};

export default SubscriptionCenterSitecore;
